import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { EMPTY, Subject, catchError, switchMap, takeUntil, tap } from 'rxjs';
import { TranslateService } from '../../services/translate.service';
import { CorporateReportingService } from '../../services/corporate-reporting.service';
import { ReportingScopeService } from '../../services/reporting-scope.service';
import { ToastService } from '../../services/toast.service';
import { SampleReport } from '../../models/corporate-reporting/sampleReport.model';
import { RiskRegisterRollup } from '../../models/corporate-reporting/riskRegisterRollup.model';
import { REGION_CODE_LABELS, getRegionCodeLabel } from '../../shared/constants/regionCode.constants';
import { translateEnum } from '../../_helpers/translate-enum.helper';
import { trackById } from '../../shared/utils/track-by.util';
import { SampleTypeEnum } from '../../enums/sampleType.enum';
import { SampleStatusEnum } from '../../enums/sampleStatus.enum';
import { SampleCensorType } from '../../enums/sampleCensorType.enum';
import { ExposureRiskEnum } from '../../enums/exposureRisk.enum';
import { AssessmentStatusEnum } from '../../enums/assessmentStatus.enum';

type SampleReportRow = SampleReport & {
    regionLabel: string;
    typeLabel: string;
    statusLabel: string;
    censorLabel: string;
};

type RiskRollupRow = RiskRegisterRollup & {
    regionLabel: string;
    exposureRiskLabel: string;
    assessmentStatusLabel: string;
    compliantLabel: string;
};

interface RegionOption {
    code: string;
    label: string;
}

const PAGE_SIZE = 50;

@Component({
  standalone: false,
    selector: 'app-corporate-reporting',
    templateUrl: './corporate-reporting.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./corporate-reporting.component.scss'],
})
export class CorporateReportingComponent implements OnInit, OnDestroy {
    readonly trackById = trackById;
    readonly pageSize = PAGE_SIZE;

    regionOptions: RegionOption[] = [];
    selectedRegionCode = '';

    sampleColumns = ['regionLabel', 'sampleNumber', 'typeLabel', 'statusLabel', 'approvedDate', 'twa', 'oel', 'dosePercentage', 'censorLabel'];
    samples: SampleReportRow[] = [];
    samplesCount = 0;
    samplesPageIndex = 0;
    samplesLoading = false;

    rollupColumns = ['regionLabel', 'numberOfWorkers', 'numberOfContractors', 'quantitativeGeometricMean', 'quantitativeGSD', 'quantitative95thPercentile', 'quantitativeResultPercentOfOEL', 'exposureRiskLabel', 'compliantLabel', 'assessmentStatusLabel'];
    rollups: RiskRollupRow[] = [];
    rollupsCount = 0;
    rollupsPageIndex = 0;
    rollupsLoading = false;

    private readonly destroy$ = new Subject<void>();
    private readonly samplesTrigger$ = new Subject<void>();
    private readonly rollupsTrigger$ = new Subject<void>();

    constructor(
        private corporateReportingService: CorporateReportingService,
        private reportingScopeService: ReportingScopeService,
        private toastService: ToastService,
        private translateService: TranslateService,
    ) {}

    ngOnInit(): void {
        this.regionOptions = this.buildRegionOptions();

        this.samplesTrigger$.pipe(
            tap(() => this.samplesLoading = true),
            switchMap(() => this.corporateReportingService.getSamples({
                regionCode: this.selectedRegionCode || undefined,
                page: this.samplesPageIndex,
                pageSize: this.pageSize,
            }).pipe(
                catchError(error => {
                    this.samplesLoading = false;
                    this.toastService.showError(error);
                    return EMPTY;
                }),
            )),
            takeUntil(this.destroy$),
        ).subscribe(response => {
            this.samples = (response?.data ?? []).map(row => this.toSampleRow(row));
            this.samplesCount = response?.count ?? 0;
            this.samplesLoading = false;
        });

        this.rollupsTrigger$.pipe(
            tap(() => this.rollupsLoading = true),
            switchMap(() => this.corporateReportingService.getRiskRollups({
                regionCode: this.selectedRegionCode || undefined,
                page: this.rollupsPageIndex,
                pageSize: this.pageSize,
            }).pipe(
                catchError(error => {
                    this.rollupsLoading = false;
                    this.toastService.showError(error);
                    return EMPTY;
                }),
            )),
            takeUntil(this.destroy$),
        ).subscribe(response => {
            this.rollups = (response?.data ?? []).map(row => this.toRollupRow(row));
            this.rollupsCount = response?.count ?? 0;
            this.rollupsLoading = false;
        });

        this.loadSamples();
        this.loadRollups();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onRegionChange(): void {
        this.samplesPageIndex = 0;
        this.rollupsPageIndex = 0;
        this.loadSamples();
        this.loadRollups();
    }

    onSamplesPage(event: PageEvent): void {
        this.samplesPageIndex = event.pageIndex;
        this.loadSamples();
    }

    onRollupsPage(event: PageEvent): void {
        this.rollupsPageIndex = event.pageIndex;
        this.loadRollups();
    }

    private loadSamples(): void {
        this.samplesTrigger$.next();
    }

    private loadRollups(): void {
        this.rollupsTrigger$.next();
    }

    private toSampleRow(row: SampleReport): SampleReportRow {
        return {
            ...row,
            regionLabel: getRegionCodeLabel(row.regionCode),
            typeLabel: translateEnum(SampleTypeEnum, row.sampleType, this.translateService),
            statusLabel: translateEnum(SampleStatusEnum, row.sampleStatus, this.translateService),
            censorLabel: translateEnum(SampleCensorType, row.sampleCensor, this.translateService),
        };
    }

    private toRollupRow(row: RiskRegisterRollup): RiskRollupRow {
        return {
            ...row,
            regionLabel: getRegionCodeLabel(row.regionCode),
            // DASH-2979 (G19): render the neutral "not assessed" state distinctly instead of blank/Low.
            exposureRiskLabel: row.exposureRisk == null
                ? this.translateService.getDynamicTranslation('Not assessed')
                : translateEnum(ExposureRiskEnum, row.exposureRisk, this.translateService),
            assessmentStatusLabel: translateEnum(AssessmentStatusEnum, row.assessmentStatus, this.translateService),
            compliantLabel: this.translateService.getDynamicTranslation(row.isCompliant ? 'Yes' : 'No'),
        };
    }

    private buildRegionOptions(): RegionOption[] {
        const codes = new Set<string>(Object.keys(REGION_CODE_LABELS));
        const home = this.reportingScopeService.homeRegionCode;
        if (home) codes.add(home);
        return Array.from(codes).map(code => ({ code, label: getRegionCodeLabel(code) }));
    }
}
